import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * One piece of class work's save engine, used by the class tile's row and the
 * record's brief alike.
 *
 * A draft saves fact by fact: a typed fact when it is left or Enter is
 * pressed (Cmd or Ctrl+Enter in Instructions), and Escape puts back the saved
 * value; a chosen fact when it is chosen. Each save sends only what changed
 * and the version it was made against, and the work it returns becomes the
 * base at once, so a second save never meets the first as a conflict.
 *
 * Published terms are never rewritten in place. Their facts are held as typed
 * until the teacher says why they changed; one correction then carries every
 * changed fact, the reason and the version. Closed work is not edited.
 *
 * Saves and the work's acts (Publish, Close, Remove) run one after another.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { assignmentsApi, type AssignmentStatus, type CorrectAssignmentRequest } from "@/lib/api/assignments";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";

import { forgetAssignment, invalidateAssignment } from "../invalidate-assignment";
import {
  KEY_OF,
  DEFAULT_DELIVERABLE,
  REASON_REQUIRED,
  WORK_FACTS,
  changesFrom,
  draftOf,
  problemsOf,
  typedFacts,
  type WorkDraft,
  type AssessmentDraft,
  type WorkFact,
  type WorkRecord,
} from "./work-facts";

/** Where a save's outcome is said: under a fact, or under the correction. */
export type SaveTarget = WorkFact | "correction";

export type SaveState =
  | { kind: "saving" }
  /** Said once when a save lands; it clears after `SAVED_SAID_FOR_MS`, never resting as a label. */
  | { kind: "saved" }
  | { kind: "failed"; reason: string | null; retry: boolean }
  | { kind: "conflict"; theirs: WorkRecord };

/** Publish's refusal while the editor shows a draft edit that did not save. */
export const UNSAVED_BEFORE_PUBLISH = a3Copy.notPublishedAChangeHereIsNotSavedRetryIt;

/** How long "Saved" stays after a save lands (object checklist item 5: an event, not a badge). */
export const SAVED_SAID_FOR_MS = 5000;

interface Status {
  target: SaveTarget;
  state: SaveState;
}

/** A refusal or an act's outcome, said once, with Refresh when reading again can help. */
export interface WorkNote {
  text: string;
  alert: boolean;
  refresh?: boolean;
}

const reasonOf = (error: unknown, fallback: string) => (error instanceof ApiError ? error.message : fallback);

/** The one sentence a refused save shows; the entry stays where it was typed. */
function refusalOf(error: ApiError): string {
  return error.status === 403
    ? a3Copy.youCanNoLongerEditThisWorkYourEditsAre
    : a3Copy.thisWorkChangedBeforeYourEditsWereSavedYourEdits;
}

export function useWorkSave(work: WorkRecord, timezone: string) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(() => draftOf(work, timezone));
  const [reason, setReasonValue] = useState("");
  const [reasonTried, setReasonTried] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [note, setNote] = useState<WorkNote | null>(null);
  const [busy, setBusy] = useState(false);
  const [removed, setRemoved] = useState(false);
  // The lifecycle follows the newest word: a read of the work, or this
  // engine's own act, whichever came last.
  const [lifecycle, setLifecycleValue] = useState<AssignmentStatus>(work.status);
  const [readStatus, setReadStatus] = useState(work.status);
  if (readStatus !== work.status) {
    setReadStatus(work.status);
    setLifecycleValue(work.status);
  }
  const mode = useRef(lifecycle);
  mode.current = lifecycle;
  const base = useRef(work);
  const typed = useRef(draft);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const pending = useRef(0);
  // A newer version is showing: nothing is sent until the editor chooses.
  const deciding = useRef(false);
  const said = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (said.current) clearTimeout(said.current); }, []);
  const { course_id: courseId } = work;

  const edit = (next: WorkDraft) => {
    typed.current = next;
    setDraft(next);
  };
  const setLifecycle = (next: AssignmentStatus) => {
    mode.current = next;
    setLifecycleValue(next);
  };

  // A newer read fills the fields only while nothing typed here is unsaved;
  // otherwise the next save meets it as a conflict. An older read never
  // replaces a save.
  useEffect(() => {
    if (!(Date.parse(work.updated_at) > Date.parse(base.current.updated_at)) || pending.current > 0) return;
    if (typedFacts(typed.current, base.current, timezone).length > 0) return;
    base.current = work;
    edit(draftOf(work, timezone));
  }, [work, timezone]);

  /** The saved work becomes the base; a fact sent or left alone takes its saved value. */
  const adopt = (saved: WorkRecord, sent: WorkDraft, sentFacts: ReadonlySet<WorkFact>) => {
    const before = draftOf(base.current, timezone);
    const after = draftOf(saved, timezone);
    base.current = saved;
    let next = typed.current;
    for (const fact of WORK_FACTS) {
      const key = KEY_OF[fact];
      const untouched = typed.current[key] === sent[key];
      const settled = sentFacts.has(fact) || sent[key] === before[key];
      if (untouched && settled && next[key] !== after[key]) next = { ...next, [key]: after[key] };
    }
    if (next !== typed.current) edit(next);
    setLifecycle(saved.status);
  };

  const enqueue = <T,>(job: () => Promise<T>): Promise<T> => {
    pending.current += 1;
    const run = queue.current.then(job).finally(() => {
      pending.current -= 1;
    });
    queue.current = run.catch(() => undefined);
    return run;
  };

  /** One PATCH at the version the base was read. True when it was saved. */
  const write = async (target: SaveTarget, body: CorrectAssignmentRequest, sentFacts: ReadonlySet<WorkFact>) => {
    const sent = typed.current;
    setNote(null);
    setStatus({ target, state: { kind: "saving" } });
    try {
      const saved = await assignmentsApi.correct(base.current.id, {
        ...body,
        expected_updated_at: base.current.updated_at,
      });
      adopt(saved, sent, sentFacts);
      setStatus({ target, state: { kind: "saved" } });
      if (said.current) clearTimeout(said.current);
      said.current = setTimeout(() => {
        said.current = null;
        setStatus((current) => (current?.state.kind === "saved" ? null : current));
      }, SAVED_SAID_FOR_MS);
      void invalidateAssignment(queryClient, courseId, saved.id);
      return true;
    } catch (error) {
      const theirs = currentOnConflict<WorkRecord>(error);
      deciding.current = theirs !== null;
      if (theirs) {
        setStatus({ target, state: { kind: "conflict", theirs } });
      } else if (error instanceof ApiError && error.status === 422) {
        setStatus({ target, state: { kind: "failed", reason: error.message, retry: false } });
      } else if (error instanceof ApiError && (error.status === 403 || error.status === 409)) {
        setStatus({ target, state: { kind: "failed", reason: null, retry: false } });
        setNote({ text: refusalOf(error), alert: true, refresh: true });
      } else {
        setStatus({ target, state: { kind: "failed", reason: null, retry: true } });
      }
      return false;
    }
  };

  /**
   * A draft sends what changed. A fact left with nothing of its own to save
   * sends nothing, unless `any` (Retry, Keep my edits) asks for every change.
   */
  const send = async (fact: WorkFact, any = false) => {
    if (deciding.current || mode.current !== "draft") return;
    const { body, facts } = changesFrom(typed.current, base.current, timezone, "draft");
    if (facts.size === 0) {
      setStatus((current) => (current?.state.kind === "failed" ? null : current));
      return;
    }
    const own = facts.has(fact);
    if (!own && !any) return;
    await write(own ? fact : [...facts][0], body, facts);
  };

  const save = (fact: WorkFact, any = false) => {
    void enqueue(() => send(fact, any));
  };

  const correct = async () => {
    if (deciding.current || mode.current !== "published") return;
    const { body, facts } = changesFrom(typed.current, base.current, timezone, "published");
    if (facts.size === 0) return;
    if (await write("correction", { ...body, reason: reason.trim() }, facts)) {
      setReasonValue("");
      setReasonTried(false);
    }
  };

  const setField = <K extends keyof WorkDraft>(key: K, value: WorkDraft[K]) => {
    const next = { ...typed.current, [key]: value };
    if (key === "assessment" && (value as AssessmentDraft).kind === "scored" && next.deliverable === "none") next.deliverable = DEFAULT_DELIVERABLE;
    edit(next);
    if (key === "deliverable") save("deliverable");
    if (key === "unitId") save("class");
    if (key === "gradeItemId") save("gradeItem");
  };

  const restore = (fact: WorkFact) => {
    const key = KEY_OF[fact];
    edit({ ...typed.current, [key]: draftOf(base.current, timezone)[key] });
    setStatus((current) => (current?.target === fact && current.state.kind === "failed" ? null : current));
  };

  /** A typed fact's keys: leaving it or Enter saves a draft's fact; Escape restores it. */
  const commit = (fact: WorkFact, multiline = false) => ({
    onBlur: () => save(fact),
    onKeyDown: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter" && (!multiline || event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        save(fact);
      } else if (event.key === "Escape") {
        restore(fact);
      }
    },
  });

  const saveCorrection = () => {
    setReasonTried(true);
    if (!reason.trim()) return;
    if (Object.values(problemsOf(typed.current, base.current, timezone, mode.current)).length > 0) return;
    void enqueue(correct);
  };

  const discard = () => {
    edit(draftOf(base.current, timezone));
    setReasonValue("");
    setReasonTried(false);
    setStatus(null);
    setNote(null);
    deciding.current = false;
  };

  // Keeping my edits keeps only the facts typed here; every other fact takes
  // their version, so their change to a fact left alone is not undone.
  const keepMine = () => {
    if (status?.state.kind !== "conflict") return;
    const { theirs } = status.state;
    let merged = draftOf(theirs, timezone);
    for (const fact of typedFacts(typed.current, base.current, timezone)) {
      const key = KEY_OF[fact];
      merged = { ...merged, [key]: typed.current[key] };
    }
    const { target } = status;
    base.current = theirs;
    deciding.current = false;
    setLifecycle(theirs.status);
    edit(merged);
    setStatus(null);
    if (target === "correction") {
      if (reason.trim()) void enqueue(correct);
    } else {
      save(target, true);
    }
  };

  const loadTheirs = () => {
    if (status?.state.kind !== "conflict") return;
    const { theirs } = status.state;
    base.current = theirs;
    deciding.current = false;
    setLifecycle(theirs.status);
    edit(draftOf(theirs, timezone));
    setReasonValue("");
    setReasonTried(false);
    setStatus(null);
    void invalidateAssignment(queryClient, courseId, theirs.id);
  };

  /** A work act in turn with the saves. Its failure is said in the editor. */
  const act = (job: () => Promise<void>, failure: string) => {
    setNote(null);
    setBusy(true);
    void enqueue(async () => {
      try {
        await job();
      } catch (error) {
        setNote({ text: reasonOf(error, failure), alert: true });
      } finally {
        setBusy(false);
      }
    });
  };

  /**
   * Publish once the saves before it land. A draft edit that failed or meets
   * a newer version is settled first: learners would receive the saved terms,
   * not the ones shown. The publisher says its own outcome.
   */
  const publish = (publisher: () => Promise<WorkRecord | null>) => act(async () => {
    if (mode.current === "draft" && (deciding.current || typedFacts(typed.current, base.current, timezone).length > 0)) {
      setNote({ text: UNSAVED_BEFORE_PUBLISH, alert: true });
      return;
    }
    const result = await publisher();
    if (!result) return;
    setLifecycle(result.status);
    void invalidateAssignment(queryClient, courseId, result.id);
  }, a3Copy.couldNotPublishThisWork);

  const close = () => act(async () => {
    const result = await assignmentsApi.close(base.current.id);
    setLifecycle(result.status);
    toast.success(a3Copy.closedToNewAttempts2(base.current.title));
    void invalidateAssignment(queryClient, courseId, result.id);
  }, a3Copy.couldNotCloseThisWork);

  /** Remove a draft. Nothing asks first: a draft never reached a learner. */
  const remove = (onRemoved?: () => void) => act(async () => {
    const { id, title } = base.current;
    await assignmentsApi.remove(id);
    setRemoved(true);
    toast.success(a3Copy.removed(title));
    onRemoved?.();
    void forgetAssignment(queryClient, courseId, id);
  }, a3Copy.couldNotRemoveThisWork);

  /** Run a host's act once the saves before it land. */
  const after = (job: () => void) => {
    void enqueue(async () => job());
  };

  const refresh = () => {
    setNote(null);
    void invalidateAssignment(queryClient, courseId, base.current.id);
  };

  const changed = typedFacts(draft, base.current, timezone);
  const problems = problemsOf(draft, base.current, timezone, lifecycle);
  // A draft's fact saves as it is left, so only one whose save failed or met a
  // newer version is lost by leaving; a published term is held until corrected.
  const unsettled = status?.state.kind === "failed" || status?.state.kind === "conflict";

  return {
    /** The work as last saved or read. */
    saved: base.current,
    draft,
    lifecycle,
    /** Published terms typed here and not yet corrected. */
    held: lifecycle !== "draft" && changed.length > 0,
    /** Anything typed here that leaving would lose. */
    dirty: (changed.length > 0 && (lifecycle !== "draft" || unsettled)) || reason.trim() !== "",
    problems,
    setField,
    commit,
    restore,
    reason,
    setReason: (value: string) => setReasonValue(value),
    reasonProblem: reasonTried && !reason.trim() ? REASON_REQUIRED : null,
    saveCorrection,
    discard,
    keepMine,
    loadTheirs,
    publish,
    close,
    remove,
    removed,
    after,
    refresh,
    busy,
    note,
    retry: () => {
      if (!status) return;
      if (status.target === "correction") void enqueue(correct);
      else save(status.target, true);
    },
    state: status?.state ?? null,
    /** The conflict's or failure's place: a fact, or the correction line. */
    target: status?.target ?? null,
    /** A refused save's reason, where it was saved. */
    failureOf: (target: SaveTarget): string | null => (
      status?.target === target && status.state.kind === "failed" ? status.state.reason : null
    ),
  };
}

export type WorkSave = ReturnType<typeof useWorkSave>;
